import styled from "styled-components";
import PipelineTree from "./PipelineTree";
import {
    convertFinal,
    convertFromFinal,
    ListField,
    PipelineElement,
    piplineConfig, setTasks,
    SkeletonElement,
    TaskEditorElement
} from "./PipelineConfig";
import {useEffect, useMemo, useRef, useState} from "react";
import {agent} from "../../api/agent";
import {useSearchParams} from "react-router-dom";
import TriggerEditor, {TriggerJson} from "../TriggerEditor";
import {Task} from "../../api/types";
import RepoSelector from "../table/RepoSelector";

interface Props {
    mode?: 'builder' | 'task';
    task?: Task | undefined;
    ci?: (ci: string) => void;
}

const Container = styled.div`

    margin-top: 1rem;
    display: flex;
    justify-content: center;

    .main {
        width: 90%;
    }

    .container {
        height: calc(100vh - 10rem);
        max-height: calc(100vh - 10rem);
        background-color: var(--content-box-bg);
        box-shadow: rgba(0, 0, 0, 0.24) 0px 3px 8px;
        min-width: 20rem;
        min-height: 10rem;
        display: grid;
        grid-template-columns: 40% 30% 30%;
    }

    .table {
        box-shadow: rgba(0, 0, 0, 0.24) 0px 3px 8px;
    }

    .json {
        padding: 1rem;
        overflow-x: auto;
        background-color: var(--content-box-bg);
        box-shadow: rgba(0, 0, 0, 0.24) 0px 3px 8px;
        white-space: pre-wrap;
    }

    .details {
        overflow-y: auto;
        padding: 1rem;
        box-shadow: rgba(0, 0, 0, 0.24) 0px 3px 8px;
    }

    textarea, input, select {
        background-color: var(--content-box-border);
        padding: .5rem;
        color: white;
        border: none;
    }

    .button {
        background-color: var(--secondary-blue);
        border: none;
        color: white;
        cursor: pointer;
        padding: 0.5rem;
        border-radius: 0.2rem;
        margin-left: .5rem;

        &:hover {
            background-color: var(--secondary-blue-hover);
        }
    }

`;

export default function PipelineBuilder({mode, task, ci}: Props) {

    const [active, setActive] = useState<PipelineElement | null>(null);
    const [reparent, setReparent] = useState<PipelineElement | null>(null);
    const [skeleton, setSkeleton] = useState<PipelineElement>(piplineConfig.find(value => value.type === 'skeleton')!.generateEmptyElement!());
    const [lastEmptyId, setLastEmptyId] = useState<any>({});

    const [searchParameters, setSearchParameters] = useSearchParams();

    useEffect(() => {
        if (!searchParameters.has('skeleton')) return;
        const skeletonId = searchParameters.get('skeleton');
        if (!skeletonId) return;
        agent.getSkeleton(skeletonId).then(async value => {
            if (!value) {
                console.error('Skeleton not found');
                return;
            }
            const converted = await convertFromFinal(JSON.parse(value.ci));
            setSkeleton(converted[0]);
            setSearchParameters({});

            setSelectedRepo(value.repo.repo);
        });
    }, [searchParameters]);

    useEffect(() => {
        if (ci)
            ci(convertFinal(skeleton));
    }, [skeleton]);

    useEffect(() => {
        if (mode && mode === 'task') {
            if (task) {
                convertFromFinal({
                    type: 'taskeditor',
                    id: crypto.randomUUID(),
                    name: task.name,
                    children: JSON.parse(task.ci),
                }).then(value => setSkeleton(value[0]));
                return;
            }
            setSkeleton(piplineConfig.find(value => value.type === 'taskeditor')!.generateEmptyElement!());
        }
    }, [mode]);

    const addItem = (type: string, parent: PipelineElement | null) => {
        const element = findElement(parent!.id, skeleton);
        const config = piplineConfig.find(value => value.type === parent?.type);
        if (config && config.addChildren && element) {
            config.addChildren(type, element);
        }
        setSkeleton(JSON.parse(JSON.stringify(skeleton)));
    }

    useEffect(() => {
        if (!skeleton) return;
        if (active) {
            const found = findElement(active.id, skeleton);
            if (!found) {
                setActive(null);
            } else {
                setActive(found);
            }
        }
    }, [skeleton]);

    useEffect(() => {
        setLastEmptyId({});
    }, [active]);

    const findElement = (id: string, parent: PipelineElement | null): PipelineElement | null => {
        if (!parent) return null;
        if (parent.id === id) return parent;
        const config = piplineConfig.find(value => value.type === parent.type);
        if (!config || !config.getChildren)
            return null;
        for (const child of config.getChildren(parent)) {
            const found = findElement(id, child);
            if (found) return found;
        }
        return null;
    }

    const findParent = (id: string, parent: PipelineElement | null, parent2: PipelineElement | null): PipelineElement | null => {
        if (!parent) return null;
        if (parent.id === id) return parent2;
        const config = piplineConfig.find(value => value.type === parent.type);
        if (!config || !config.getChildren)
            return null;
        for (const child of config.getChildren(parent)) {
            const found = findParent(id, child, parent);
            if (found) return found;
        }
        return null;
    }

    const skeletonRef = useRef(skeleton);

    useEffect(() => {
        skeletonRef.current = skeleton;
    }, [skeleton]);

    useEffect(() => {
        agent.getTasks().then(value => {
            if (value) {
                setTasks(value);
            }
        });
    }, []);

    useEffect(() => {

        let buffer: any[] = [];

        const callback = (data: any) => {
            buffer.push(data);
        }

        const interval = setInterval(() => {
            let buffer2: any[] = [...buffer];
            buffer = buffer.slice(buffer2.length, buffer2.length);
            for (const data of buffer2) {
                switch (data.type) {
                    case 'initial': {
                        if (data.from && data.from === 'skeleton' && skeletonRef.current.type === 'skeleton') {
                            skeletonRef.current.id = data.id;
                            console.log('Skeleton initialized with id:', data.id);
                        }
                        let element = findElement(data.id, skeletonRef.current);
                        if (!element) {
                            console.error('Element not found for id:', data.id);
                            break;
                        }
                        element.lastStatus = data.status;
                        element.logs = data.logs;
                        break;
                    }
                    case 'status': {
                        let element = findElement(data.pipelineId, skeletonRef.current);
                        if (!element) {
                            console.error('Element not found for id:', data.pipelineId);
                            break;
                        }
                        element.lastStatus = data.status;
                        break;
                    }
                    case 'log': {
                        let element = findElement(data.pipelineId, skeletonRef.current);
                        if (!element) {
                            console.error('Element not found for id:', data.pipelineId);
                            break;
                        }
                        if (!element.logs) {
                            element.logs = [];
                        }
                        element.logs.push({
                            level: data.level,
                            message: data.message
                        });
                        break;
                    }
                }
            }
            if (buffer2.length > 0) {
                setSkeleton(JSON.parse(JSON.stringify(skeletonRef.current)));
            }
        }, 25);

        const id = agent.subscribe('/testUpdate', callback);

        return () => {
            agent.unsubscribe(id);
            clearInterval(interval);
        };
    }, []);

    const json = useMemo(() => {
        return JSON.stringify((convertFinal((skeleton as SkeletonElement)) as any).children, null, 2);
    }, [skeleton]);

    const activeConfig = useMemo(() => {
        if (!active) return null;
        return piplineConfig.find(value => value.type === active.type);
    }, [active]);

    const moveUp = (element: PipelineElement) => {
        let parent = findParent(element.id, skeleton, null);
        if (!parent) return;
        const config = piplineConfig.find(value => value.type === parent!.type);
        if (!config || !config.moveChildren) return;
        config.moveChildren(parent, config.getChildren!(parent).indexOf(element), config.getChildren!(parent).indexOf(element) - 1);
        setSkeleton(JSON.parse(JSON.stringify(skeleton)));
    }

    const moveDown = (element: PipelineElement) => {
        let parent = findParent(element.id, skeleton, null);
        if (!parent) return;
        const config = piplineConfig.find(value => value.type === parent!.type);
        if (!config || !config.moveChildren) return;
        config.moveChildren(parent, config.getChildren!(parent).indexOf(element), config.getChildren!(parent).indexOf(element) + 1);
        setSkeleton(JSON.parse(JSON.stringify(skeleton)));
    }

    const removeItem = (element: PipelineElement) => {
        if (active && active.id === element.id) {
            setActive(null);
        }
        let parent = findParent(element.id, skeleton, null);
        if (!parent) return;
        const config = piplineConfig.find(value => value.type === parent!.type);
        if (!config || !config.removeChildren) return;
        config.removeChildren(parent, element);
        setSkeleton(JSON.parse(JSON.stringify(skeleton)));
    }

    const onRun = (element: PipelineElement) => {
        new Promise<void>(async resolve => {
            const wId = crypto.randomUUID();
            if (mode === 'task') {
                await agent.setupTest([
                    {
                        type: 'worker',
                        image: 'build-container',
                        name: 'task-test',
                        id: skeleton.id,
                        children: convertFinal(skeleton as TaskEditorElement).children,
                    }
                ]);
            } else {
                await agent.setupTest(convertFinal(skeleton as SkeletonElement).children);
            }
            const parent = findParent(element.id, skeleton, null);

            let additionalChildren: PipelineElement[] = [];
            if (parent) {
                const config = piplineConfig.find(value => value.type === parent!.type);
                const index = config!.getChildren!(parent).indexOf(element);
                if (index < 0) return;
                for (let i = 0; i < index; i++) {
                    const child = config!.getChildren!(parent)[i];
                    if (child.type === 'worker')
                        continue;
                    if (child.lastStatus === 'finished')
                        continue;
                    additionalChildren.push(child);
                    console.log('Adding above child', child.id);
                }
            }

            for (const parent of getAllParents(element)) {
                /*if (parent.lastStatus === 'finished')
                    continue;*/
                additionalChildren.push(parent);
                console.log('Adding parent', parent.id);
            }
            for (const child of getAllChildren(element)) {
                additionalChildren.push(child);
                console.log('Adding child', child.id);
            }

            additionalChildren.push(element);
            if (mode === 'task') {
                additionalChildren.push({
                    id: wId,
                } as TaskEditorElement);
            }

            agent.runTest(additionalChildren.map(value => value.id), element.id, selectedRepo).then(value => {
                console.log('Test run started', value);
            });
            console.log(additionalChildren.map(value => value.id));
            resolve();
        }).then(value => {

        })

    }

    const getAllChildren = (element: PipelineElement, children: PipelineElement[] = []): PipelineElement[] => {
        const config = piplineConfig.find(value => value.type === element.type);
        if (!config || !config.getChildren) return children;
        for (const child of config.getChildren(element)) {
            children.push(child);
            getAllChildren(child, children);
        }
        return children;
    }

    const getAllParents = (element: PipelineElement, parents: PipelineElement[] = []): PipelineElement[] => {
        const parent = findParent(element.id, skeleton, null);
        if (parent) {
            parents.push(parent);
            return getAllParents(parent, parents);
        }
        return parents;
    }

    const [encryptValue, setEncryptValue] = useState<string>('');
    const [selectedRepo, setSelectedRepo] = useState<string>('');


    return (
        <>
            <Container>
                <div className={'main'}>
                    <div>
                        <RepoSelector selectedRepo={selectedRepo} setSelectedRepo={(val) => setSelectedRepo(val!)}></RepoSelector>
                        <div style={{float: 'right'}}>
                            <label>{encryptValue.startsWith("ENC[v1]:") ? 'Decrypt' : 'Encrypt'}:</label>
                            <input type={'text'} value={encryptValue} onChange={e => setEncryptValue(e.target.value)}/>
                            <button className={'button'} onClick={() => {
                                if (encryptValue.startsWith("ENC[v1]:")) {
                                    agent.decrypt(encryptValue).then((value) => {
                                        //copy value to clipboard
                                        navigator.clipboard.writeText(value.decryptedData);
                                        setEncryptValue(value.decryptedData);
                                    })
                                } else {
                                    agent.encrypt(encryptValue).then(value => {
                                        //copy value to clipboard
                                        navigator.clipboard.writeText(value.encryptedData);
                                        setEncryptValue(value.encryptedData);
                                    });
                                }
                            }}>{encryptValue.startsWith("ENC[v1]:") ? 'Decrypt' : 'Encrypt'}</button>
                        </div>

                    </div>
                    <div className={'container'}>
                        <div className={'table'} style={{height: '100%', overflowY: 'auto'}}>
                            <PipelineTree current={skeleton} active={active} setActive={(element) => {
                                if (!reparent) {
                                    if (active && active.id === element.id) {
                                        setActive(null);
                                        return;
                                    }
                                    setActive(element);
                                    return;
                                }
                                if (element.id === reparent.id) {
                                    setReparent(null);
                                    return;
                                }
                                const parent = findParent(reparent.id, skeleton, null);
                                if (!parent) {
                                    console.error('Parent not found for element', reparent.id);
                                    return;
                                }

                                const target = findElement(reparent.id, skeleton);
                                if (!target) {
                                    console.error('Target not found for element', reparent.id);
                                    return;
                                }

                                const config = piplineConfig.find(value => value.type === parent.type);
                                const config1 = piplineConfig.find(value => value.type === element.type);
                                const config2 = piplineConfig.find(value => value.type === target.type);
                                if (!config || !config.removeChildren) {
                                    console.error('Config not found for element', parent.type);
                                    return;
                                }
                                if (!config1 || !config1.addChildren2) {
                                    console.error('Config not found for element', element.type);
                                    return;
                                }
                                if (!config2) {
                                    console.error('Config not found for element', target.type);
                                    return;
                                }
                                if (config2.canBeRemoved && !config2.canBeRemoved(element)) {
                                    return;
                                }
                                config.removeChildren(parent, target);
                                config1.addChildren2(element, target);


                                setReparent(null);
                            }}
                                          addItem={addItem} moveUp={moveUp} moveDown={moveDown}
                                          removeItem={removeItem} onRun={onRun} reparent={reparent}
                                          setReparent={setReparent} moveFully={(element, dir) => {
                                if (!active) {
                                    console.error('No active element to move');
                                    return;
                                }
                                const activeParent = findParent(active.id, skeleton, null);
                                const activeElement = findElement(active.id, skeleton);
                                const clickedElement = findElement(element.id, skeleton);
                                const clickedParent = findParent(element.id, skeleton, null);
                                if (!activeParent || !activeElement || !clickedElement || !clickedParent) {
                                    console.error('Active or clicked element not found', activeParent, activeElement, clickedElement, clickedParent);
                                    return;
                                }

                                if (clickedElement.id === activeElement.id) {
                                    //clicked the same element, so we just set it as active
                                    setActive(clickedElement);
                                    return;
                                }

                                const activeParentConfig = piplineConfig.find(value => value.type === activeParent.type);
                                const activeElementConfig = piplineConfig.find(value => value.type === activeElement.type);
                                const clickedElementConfig = piplineConfig.find(value => value.type === clickedElement.type);
                                const clickedParentConfig = piplineConfig.find(value => value.type === clickedParent.type);
                                if (!activeParentConfig || !activeElementConfig || !clickedElementConfig || !clickedParentConfig) {
                                    console.error('Config not found for element', activeParent.type, activeElement.type, clickedElement.type, clickedParent.type);
                                    return;
                                }

                                if (activeParent !== clickedParent) {
                                    //move the active element to the clicked parent
                                    if (activeParentConfig.removeChildren && clickedParentConfig.addChildren2) {
                                        activeParentConfig.removeChildren(activeParent, activeElement);
                                        clickedParentConfig.addChildren2(clickedParent, activeElement);
                                    }
                                }

                                if (clickedParentConfig.moveChildren) {
                                    const index = clickedParentConfig.getChildren!(clickedParent).indexOf(clickedElement);
                                    if (index < 0) return;
                                    let oldIndex = clickedParentConfig.getChildren!(clickedParent).findIndex(value => value.id === activeElement.id);
                                    if (dir === 'before') {
                                        clickedParentConfig.moveChildren(clickedParent, oldIndex, oldIndex < index ? index - 1 : index);
                                    } else if (dir === 'after') {
                                        clickedParentConfig.moveChildren(clickedParent, oldIndex, oldIndex < index ? index : index + 1);
                                    }
                                    setSkeleton(JSON.parse(JSON.stringify(skeleton)));
                                } else {
                                    console.error('Clicked parent config does not have moveChildren method', clickedParent.type);
                                }
                            }}></PipelineTree>
                        </div>
                        <div className={'details'}>
                            {!active && (<div>
                                <h1>Select an element to edit its properties</h1>
                            </div>)}
                            {active && (
                                <div>
                                    <h1>{active.type}</h1>
                                    <div style={{display: 'flex', flexDirection: 'column', gap: '1.5rem'}}>
                                        {activeConfig && findElement(active.id, skeleton) && activeConfig.fields ? (Object.keys(activeConfig.fields).map((name, index) => {
                                            const field = activeConfig.fields![name];
                                            switch (field.type) {
                                                case 'string': {
                                                    return (
                                                        <div key={index}>
                                                            <label>{field.label}</label>
                                                            <input
                                                                style={{marginLeft: '1rem', width: 'calc(100% - 2rem)'}}
                                                                type="text"
                                                                value={(findElement(active.id, skeleton)! as any)[name] || ''}
                                                                placeholder={field.placeholder}
                                                                onChange={(e) => {
                                                                    (findElement(active.id, skeleton)! as any)[name] = e.target.value;
                                                                    setSkeleton(JSON.parse(JSON.stringify(skeleton)));
                                                                }}
                                                            />
                                                        </div>
                                                    );
                                                }
                                                case 'dropdown': {
                                                    if (name === 'image' && (findParent(active.id, skeleton, null)?.type ?? '') !== 'skeleton') {
                                                        return (<></>);
                                                    }
                                                    return (
                                                        <div key={index}>
                                                            <label>{field.label}</label>
                                                            <select
                                                                style={{marginLeft: '1rem'}}
                                                                value={(findElement(active.id, skeleton)! as any)[name] || 'none'}
                                                                onChange={(e) => {
                                                                    if (e.target.value === 'none') {
                                                                        delete (findElement(active.id, skeleton)! as any)[name];
                                                                    } else {
                                                                        (findElement(active.id, skeleton)! as any)[name] = e.target.value;
                                                                    }
                                                                    setSkeleton(JSON.parse(JSON.stringify(skeleton)));
                                                                }}
                                                            >
                                                                {field.options!.map((option, optionIndex) => (
                                                                    <option key={optionIndex} value={option}>
                                                                        {option}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                    );
                                                }
                                                case 'variable': {
                                                    //should have a key and a value pair, and auto generate a new rows at the end so the user can fill in more and remove empty rows inbetween (variables is an object with key and value properties)
                                                    const variables = (findElement(active.id, skeleton)! as any)[name] || [];
                                                    let lastEmpty = lastEmptyId[name];
                                                    if (!lastEmpty) {
                                                        lastEmpty = crypto.randomUUID();
                                                        setLastEmptyId({...lastEmptyId, [name]: lastEmpty});
                                                    }
                                                    return (
                                                        <div key={index}>
                                                            <label>{field.label}</label>
                                                            {[...variables, {
                                                                key: '',
                                                                value: '',
                                                                id: lastEmpty
                                                            }].map((key, index) => (
                                                                <div key={key.id}
                                                                     style={{
                                                                         display: 'flex',
                                                                         gap: '0.5rem',
                                                                         marginTop: '0.5rem'
                                                                     }}>
                                                                    <input
                                                                        type="text"
                                                                        placeholder="Key"
                                                                        value={key.key}
                                                                        onChange={(e) => {
                                                                            const newVariables = [...variables];
                                                                            let variable = newVariables.find((e) => e.id === key.id);
                                                                            if (!variable && e.target.value.length > 0) {
                                                                                variable = {
                                                                                    key: e.target.value.length,
                                                                                    value: '',
                                                                                    id: key.id
                                                                                };
                                                                                newVariables.push(variable);
                                                                                setLastEmptyId({
                                                                                    ...lastEmptyId,
                                                                                    [name]: crypto.randomUUID()
                                                                                });
                                                                            }
                                                                            if (variable) {
                                                                                variable.key = e.target.value;
                                                                            }

                                                                            (findElement(active.id, skeleton)! as any)[name] = newVariables;
                                                                            setSkeleton(JSON.parse(JSON.stringify(skeleton)));
                                                                        }}

                                                                        onBlur={(e) => {
                                                                            if (e.target.value.length === 0) {
                                                                                const newVariables = variables.filter((v: any) => v.id !== key.id);
                                                                                (findElement(active.id, skeleton)! as any)[name] = newVariables;
                                                                                setSkeleton(JSON.parse(JSON.stringify(skeleton)));
                                                                            }
                                                                        }}
                                                                    />
                                                                    <i className="fa-solid fa-arrow-right"
                                                                       style={{marginTop: '.5rem'}}></i>
                                                                    <input
                                                                        type="text"
                                                                        placeholder="Value"
                                                                        value={key.value}
                                                                        onChange={(e) => {
                                                                            const newVariables = [...variables];
                                                                            let variable = newVariables.find((e) => e.id === key.id);
                                                                            if (!variable && e.target.value.length > 0) {
                                                                                variable = {
                                                                                    key: String(index),
                                                                                    value: e.target.value,
                                                                                    id: key.id
                                                                                };
                                                                                newVariables.push(variable);
                                                                                setLastEmptyId({
                                                                                    ...lastEmptyId,
                                                                                    [name]: crypto.randomUUID()
                                                                                });
                                                                            }
                                                                            if (variable) {
                                                                                variable.value = e.target.value;
                                                                            }

                                                                            (findElement(active.id, skeleton)! as any)[name] = newVariables;
                                                                            setSkeleton(JSON.parse(JSON.stringify(skeleton)));
                                                                        }}
                                                                    />
                                                                    <div
                                                                        style={{marginTop: '.25rem', cursor: 'pointer'}}
                                                                        onClick={async () => {
                                                                            const wasEncrypted = key.value.startsWith('ENC[v1]:');
                                                                            let newValue = key.value;
                                                                            if (wasEncrypted) {
                                                                                newValue = (await agent.decrypt(key.value)).decryptedData;
                                                                            } else {
                                                                                newValue = (await agent.encrypt(key.value)).encryptedData;
                                                                            }

                                                                            {
                                                                                const newVariables = [...variables];
                                                                                let variable = newVariables.find((e) => e.id === key.id);
                                                                                if (!variable && newValue > 0) {
                                                                                    variable = {
                                                                                        key: String(index),
                                                                                        value: newValue,
                                                                                        id: key.id
                                                                                    };
                                                                                    newVariables.push(variable);
                                                                                    setLastEmptyId({
                                                                                        ...lastEmptyId,
                                                                                        [name]: crypto.randomUUID()
                                                                                    });
                                                                                }
                                                                                if (variable) {
                                                                                    variable.value = newValue;
                                                                                }

                                                                                (findElement(active.id, skeleton)! as any)[name] = newVariables;
                                                                                setSkeleton(JSON.parse(JSON.stringify(skeleton)));
                                                                            }

                                                                        }}>
                                                                        {!key.value.startsWith('ENC[v1]:') && (
                                                                            <i className="fa-solid fa-lock"></i>)}
                                                                        {key.value.startsWith('ENC[v1]:') && (
                                                                            <i className="fa-solid fa-lock-open"></i>)}
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )

                                                }
                                                case "list": {
                                                    const list = (findElement(active.id, skeleton)! as any)[name] || [];
                                                    let lastEmpty = lastEmptyId[name];
                                                    if (!lastEmpty) {
                                                        lastEmpty = crypto.randomUUID();
                                                        setLastEmptyId({...lastEmptyId, [name]: lastEmpty});
                                                    }
                                                    return (
                                                        <div key={index}>
                                                            <label>{field.label}</label>
                                                            <ul>
                                                                {[...list, {
                                                                    value: '',
                                                                    id: lastEmpty
                                                                }].map((item: ListField, itemIndex: number) => (
                                                                    <li key={item.id} style={{marginTop: '0.5rem'}}>
                                                                        <input
                                                                            type="text"
                                                                            value={item.value}
                                                                            onChange={(e) => {
                                                                                const newList = [...list];
                                                                                let variable = newList.find((e) => e.id === item.id);
                                                                                if (!variable && e.target.value.length > 0) {
                                                                                    variable = {
                                                                                        value: e.target.value,
                                                                                        id: item.id
                                                                                    };
                                                                                    newList.push(variable);
                                                                                    setLastEmptyId({
                                                                                        ...lastEmptyId,
                                                                                        [name]: crypto.randomUUID()
                                                                                    });
                                                                                }
                                                                                if (variable) {
                                                                                    variable.value = e.target.value;
                                                                                }
                                                                                (findElement(active.id, skeleton)! as any)[name] = newList;
                                                                                setSkeleton(JSON.parse(JSON.stringify(skeleton)));
                                                                                setLastEmptyId({
                                                                                    ...lastEmptyId,
                                                                                    [name]: crypto.randomUUID()
                                                                                });
                                                                            }}
                                                                            onBlur={(e) => {
                                                                                if (e.target.value.length === 0) {
                                                                                    const newList = list.filter((v: any) => v.id !== item.id);
                                                                                    (findElement(active.id, skeleton)! as any)[name] = newList;
                                                                                    setSkeleton(JSON.parse(JSON.stringify(skeleton)));
                                                                                }
                                                                            }}
                                                                        />
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                    );
                                                }
                                                case 'multiselect': {
                                                    const selectedValues = (findElement(active.id, skeleton)! as any)[name] || [];
                                                    return (
                                                        <div key={index} style={{display: 'flex', gap: '0.5rem'}}>
                                                            <label>{field.label}</label>
                                                            <select
                                                                style={{marginLeft: '1rem'}}
                                                                multiple
                                                                value={selectedValues}
                                                                onChange={(e) => {
                                                                    (findElement(active.id, skeleton)! as any)[name] = Array.from(e.target.selectedOptions, option => option.value);
                                                                    setSkeleton(JSON.parse(JSON.stringify(skeleton)));
                                                                }}
                                                            >
                                                                {field.options!.map((option, optionIndex) => (
                                                                    <option key={optionIndex} value={option}>
                                                                        {option}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                    );
                                                }
                                                case 'cmd': {
                                                    return (
                                                        <div key={index}>
                                                            <label>{field.label}</label>
                                                            <textarea
                                                                style={{
                                                                    marginLeft: '1rem',
                                                                    width: 'calc(100% - 2rem)',
                                                                    height: '5rem'
                                                                }}

                                                                value={(findElement(active.id, skeleton)! as any)[name] || ''}
                                                                placeholder={field.placeholder}
                                                                onChange={(e) => {
                                                                    (findElement(active.id, skeleton)! as any)[name] = e.target.value;
                                                                    setSkeleton(JSON.parse(JSON.stringify(skeleton)));
                                                                }}
                                                            />
                                                        </div>
                                                    )
                                                }
                                                case 'trigger': {


                                                    return (
                                                        <TriggerEditor
                                                            value={(findElement(active.id, skeleton)! as any)[name] || {}}
                                                            onChange={(newTrigger: TriggerJson | null) => {
                                                                (findElement(active.id, skeleton)! as any)[name] = newTrigger;
                                                                setSkeleton(JSON.parse(JSON.stringify(skeleton)));
                                                            }}/>
                                                    );
                                                }
                                                case 'repo': {

                                                    return (
                                                        <div key={index}>
                                                            <label>{field.label}</label>
                                                            <div>
                                                                <RepoSelector selectedRepo={(findElement(active.id, skeleton)! as any)[name] as any as string} setSelectedRepo={(repo) => {
                                                                    (findElement(active.id, skeleton)! as any)[name] = repo;
                                                                    setSkeleton(JSON.parse(JSON.stringify(skeleton)));
                                                                }}></RepoSelector>
                                                            </div>
                                                        </div>
                                                    )
                                                }
                                            }
                                            return <></>
                                        })) : <></>}
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className={'json'}>
                            <h1>JSON Representation</h1>
                            <pre contentEditable={true}
                                 style={{overflowY: 'auto', maxHeight: 'calc(100% - 6rem)', height: '100%'}}
                                 onBlur={(e => {

                                     try {
                                         if (mode === 'task') {
                                             convertFromFinal({
                                                 type: 'taskeditor',
                                                 id: crypto.randomUUID(),
                                                 name: task?.name ?? '',
                                                 children: JSON.parse(e.currentTarget.innerText.replaceAll('\n', ''))
                                             }).then(value => setSkeleton(value[0]));
                                         } else {
                                             convertFromFinal({
                                                 type: 'skeleton',
                                                 id: crypto.randomUUID(),
                                                 children: JSON.parse(e.currentTarget.innerText.replaceAll('\n', ''))
                                             }).then(value => setSkeleton(value[0]));
                                         }
                                     } catch (error) {
                                         console.error('Invalid JSON format', error);
                                         alert('Invalid JSON format');
                                     }
                                 })}>{json}</pre>
                        </div>
                    </div>
                </div>
            </Container>
        </>
    );
}